import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";

// Import Shadcn components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import RecurrenteIntegrationCard from "./components/RecurrenteIntegrationCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Components
const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasWelcomed, setHasWelcomed] = useState(false);

  // Mensaje de bienvenida automático
  const welcomeMessage = {
    text: "¡Hola! 👋 Soy el asistente virtual de ProtegeYA. ¿En qué puedo ayudarte hoy?",
    sender: 'ai',
    timestamp: new Date()
  };

  // Mostrar mensaje de bienvenida al abrir el chat
  const handleOpenChat = () => {
    setIsOpen(true);
    if (!hasWelcomed && chatHistory.length === 0) {
      setChatHistory([welcomeMessage]);
      setHasWelcomed(true);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    const userMessage = { text: message, sender: 'user', timestamp: new Date() };
    setChatHistory(prev => [...prev, userMessage]);
    
    try {
      const response = await axios.post(`${API}/whatsapp/chat`, {
        message: message.trim(),
        session_id: 'web-chat-' + Date.now()
      });
      
      const aiMessage = { 
        text: response.data.response, 
        sender: 'ai', 
        timestamp: new Date() 
      };
      setChatHistory(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = { 
        text: "Lo siento, hay un problema técnico. Por favor intenta más tarde.", 
        sender: 'ai', 
        timestamp: new Date() 
      };
      setChatHistory(prev => [...prev, errorMessage]);
    }
    
    setMessage("");
    setIsLoading(false);
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={handleOpenChat}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-lg transform hover:scale-105 transition-all duration-200"
          style={{backgroundColor: '#22C55E'}}
          data-testid="whatsapp-widget-button"
        >
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
        </Button>
      </div>

      {/* Chat Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[600px] p-0" data-testid="whatsapp-chat-dialog">
          {/* Header mejorado */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-lg">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-white text-lg font-semibold">Asistente ProtegeYA</DialogTitle>
                  <p className="text-green-100 text-xs">En línea</p>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="flex flex-col h-[480px] bg-gray-50">
            {/* Chat messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d1d5db' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}>
              {chatHistory.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`px-4 py-3 rounded-2xl max-w-[75%] shadow-sm ${
                      msg.sender === 'user'
                        ? 'bg-green-500 text-white rounded-tr-none'
                        : 'bg-white text-gray-800 rounded-tl-none border border-gray-200'
                    }`}
                    style={{
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                    <span className={`text-[10px] mt-1 block ${
                      msg.sender === 'user' ? 'text-green-100' : 'text-gray-400'
                    }`}>
                      {new Date(msg.timestamp).toLocaleTimeString('es-GT', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-800 px-4 py-3 rounded-2xl rounded-tl-none max-w-xs shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                      <span className="text-xs text-gray-500">Escribiendo...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="border-t border-gray-200 px-4 py-3 bg-white rounded-b-lg">
              <div className="flex gap-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  disabled={isLoading}
                  data-testid="whatsapp-chat-input"
                  className="flex-1 border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-full px-4"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={isLoading || !message.trim()}
                  data-testid="whatsapp-chat-send"
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6"
                  style={{backgroundColor: isLoading || !message.trim() ? '#9CA3AF' : '#22C55E'}}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Función helper para convertir URLs de YouTube al formato embed con autoplay y loop
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    let videoId = null;
    
    // Extraer video ID de diferentes formatos de YouTube
    if (url.includes('youtube.com/watch')) {
      videoId = new URL(url).searchParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('/embed/')[1].split('?')[0];
    }
    
    // Si es un video de YouTube, agregar parámetros de autoplay y loop
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&modestbranding=1`;
    }
    
    // Para otras URLs (Vimeo, etc.) o videos locales, retornar tal cual
    return url;
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBuster = `?t=${Date.now()}`;
        const [contentRes, plansRes, publicSettingsRes] = await Promise.all([
          axios.get(`${API}/content/home${cacheBuster}`),
          axios.get(`${API}/plans${cacheBuster}`),
          axios.get(`${API}/settings/public${cacheBuster}`).catch(() => ({ data: { logo: null } }))
        ]);
        setContent(contentRes.data);
        setPlans(plansRes.data);
        setSettings(publicSettingsRes.data);
      } catch (error) {
        console.error('Error fetching content:', error);
        toast.error('Error cargando el contenido');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const heroVideo = process.env.REACT_APP_HERO_VIDEO_URL ? 
    { url: process.env.REACT_APP_HERO_VIDEO_URL, title: "Video de Prueba" } : 
    content?.hero?.videos?.[0];
  const heroTexts = content?.hero?.texts || {};
  const comoFuncionaImages = content?.como_funciona?.images || [];
  const comoFuncionaTexts = content?.como_funciona?.texts || {};

  const handlePlanCheckout = (plan) => {
    // Determinar el tipo de plan basado en el nombre
    let planType = 'mensual';
    if (plan.name.toLowerCase().includes('semestral')) {
      planType = 'semestral';
    } else if (plan.name.toLowerCase().includes('anual')) {
      planType = 'anual';
    }
    
    // Redirigir a la página de registro con el plan como parámetro
    window.location.href = `/registro-corredor?plan=${planType}`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-green-50 min-h-screen flex items-center" style={{background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)'}}>
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4 mb-8">
                  <img 
                    src={settings?.logo?.url || "https://customer-assets.emergentagent.com/job_autoprosub/artifacts/5hlnpb9c_WhatsApp%20Image%202025-09-29%20at%206.13.45%20PM.jpeg"} 
                    alt="ProtegeYA Logo" 
                    className={settings?.logo?.height || "h-24"} 
                    style={{width: settings?.logo?.width === 'w-auto' ? 'auto' : settings?.logo?.width}}
                  />
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight" data-testid="hero-title" style={{color: '#1E3A8A'}}>
                  {heroTexts.title ? (
                    <span dangerouslySetInnerHTML={{ __html: heroTexts.title.replace(/# /, '').replace(/\*\*/g, '<strong>').replace(/\*/g, '<em>') }} />
                  ) : (
                    'Protege Tu Futuro con ProtegeYA'
                  )}
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed" data-testid="hero-subtitle">
                  {heroTexts.subtitle || 'La plataforma de seguros más confiable de Guatemala. Protección integral para ti y tu familia con la mejor tecnología y atención personalizada.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg rounded-full transform hover:scale-105 transition-all duration-200 shadow-lg"
                  style={{backgroundColor: '#22C55E'}}
                  data-testid="cta-subscribe-button"
                  onClick={() => {
                    // Scroll to plans section
                    document.querySelector('[data-testid="plans-section"]')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {heroTexts.cta_primary || 'Protégete Ahora'}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-green-500 text-green-600 hover:bg-green-50 px-8 py-4 text-lg rounded-full transform hover:scale-105 transition-all duration-200"
                  style={{borderColor: '#22C55E', color: '#16A34A'}}
                  data-testid="cta-whatsapp-button"
                  onClick={() => {
                    // Open WhatsApp with pre-filled message
                    const message = encodeURIComponent('Hola, me interesa conocer más sobre los planes de protección de ProtegeYA');
                    window.open(`https://wa.me/50243821122?text=${message}`, '_blank');
                  }}
                >
                  {heroTexts.cta_secondary || 'Contáctanos'}
                </Button>
              </div>
            </div>
            
            <div className="relative">
              {heroVideo ? (
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white p-4">
                  {heroVideo.url.includes('youtube.com') || heroVideo.url.includes('youtu.be') || heroVideo.url.includes('vimeo.com') ? (
                    <iframe
                      src={getEmbedUrl(heroVideo.url)}
                      className="w-full h-64 lg:h-80 rounded-xl"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={heroVideo.title || "Video Hero"}
                      data-testid="hero-video"
                    ></iframe>
                  ) : (
                    <video
                      src={heroVideo.url.startsWith('http') ? heroVideo.url : `${BACKEND_URL}${heroVideo.url}`}
                      className="w-full h-64 lg:h-80 rounded-xl object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      title={heroVideo.title || "Video Hero"}
                      data-testid="hero-video"
                      onError={(e) => {
                        console.error('Video loading error:', e);
                        e.target.style.display = 'none';
                        const nextSibling = e.target.nextSibling;
                        if (nextSibling) {
                          nextSibling.style.display = 'flex';
                        }
                      }}
                    >
                      Tu navegador no soporta el elemento video.
                    </video>
                  )}
                  <div className="bg-green-100 rounded-xl h-64 lg:h-80 flex items-center justify-center hidden" style={{backgroundColor: '#F0FDF4'}}>
                    <p className="text-green-600" style={{color: '#22C55E'}}>Error cargando video</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-100 rounded-2xl h-64 lg:h-80 flex items-center justify-center" style={{backgroundColor: '#F0FDF4'}}>
                  <p className="text-green-600" style={{color: '#22C55E'}}>Video Principal</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section className="py-20 bg-white" data-testid="como-funciona-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{color: '#1E3A8A'}}>
              {comoFuncionaTexts.title ? 
                comoFuncionaTexts.title.replace(/## /, '') : 
                '¿Cómo Funciona ProtegeYA?'
              }
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {comoFuncionaTexts.subtitle || 'Un proceso simple y seguro diseñado para brindarte la mejor protección'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: comoFuncionaTexts.step1_title || "Únete a ProtegeYA",
                description: comoFuncionaTexts.step1_description || "Regístrate como corredor y activá el plan que se ajuste a tu capacidad de atención y cierre",
                image: "https://xwgc22pwui.us-east-1.awsapprunner.com/api/uploads/images/paso1-default.jpeg"
              },
              {
                step: "2", 
                title: comoFuncionaTexts.step2_title || "Recibe Leads Calificados",
                description: comoFuncionaTexts.step2_description || "Te enviamos contactos reales que ya cotizaron y están listos para contratar su seguro",
                image: "https://xwgc22pwui.us-east-1.awsapprunner.com/api/uploads/images/paso2-default.jpeg"
              },
              {
                step: "3",
                title: comoFuncionaTexts.step3_title || "Cerrá la venta y crecé", 
                description: comoFuncionaTexts.step3_description || "Contactá al lead, asesoralo y cerrá la contratación. Vos ganás, el cliente se protege",
                image: "https://xwgc22pwui.us-east-1.awsapprunner.com/api/uploads/images/paso3-default.jpeg"
              }
            ].map((item, index) => (
              <Card key={index} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="absolute top-4 left-4 z-10">
                  <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold" style={{backgroundColor: '#22C55E'}}>
                    {item.step}
                  </div>
                </div>
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="plans-section" style={{background: 'linear-gradient(135deg, #F9FAFB 0%, #EFF6FF 100%)'}}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{color: '#1E3A8A'}}>
              {content?.planes?.texts?.title ? content.planes.texts.title.replace(/^#+\s*/, '') : 'Planes de Protección ProtegeYA'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content?.planes?.texts?.subtitle || 'Encuentra la protección perfecta para ti y tu familia'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl ${
                  plan.is_popular ? 'ring-2 ring-green-500 scale-105' : ''
                }`}
                style={{borderColor: plan.is_popular ? '#22C55E' : undefined}}
              >
                {plan.is_popular && (
                  <div className="absolute top-0 left-0 right-0">
                    <div className="text-white text-center py-2 text-sm font-semibold" style={{backgroundColor: '#22C55E'}}>
                      MÁS POPULAR
                    </div>
                  </div>
                )}
                
                <CardHeader className={plan.is_popular ? 'pt-12' : 'pt-6'}>
                  <CardTitle className="text-xl font-bold leading-tight" style={{color: '#1E3A8A'}}>
                    {plan.name.includes('Mensual') ? '📱 Plan Mensual' :
                     plan.name.includes('Semestral') ? '🚀 Plan Semestral' :
                     plan.name.includes('Anual') ? '👑 Plan Anual' :
                     plan.name.split(' - ')[0]}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="text-4xl font-bold" style={{color: '#22C55E'}}>Q{plan.price_q_monthly}</span>
                      <span className="text-gray-500">/mes</span>
                    </div>
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start gap-3">
                        <div className="rounded-full p-1 mt-0.5" style={{backgroundColor: '#F0FDF4'}}>
                          <svg className="w-3 h-3" fill="#22C55E" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full mt-6 rounded-full py-4 px-4 text-sm font-semibold transition-all duration-200 whitespace-normal leading-tight ${
                      plan.is_popular
                        ? 'text-white'
                        : 'bg-white text-white hover:opacity-90'
                    }`}
                    style={{
                      backgroundColor: plan.is_popular ? '#22C55E' : '#1E3A8A',
                      borderWidth: plan.is_popular ? '0' : '2px',
                      borderColor: plan.is_popular ? 'transparent' : '#22C55E',
                      minHeight: '60px',
                      height: 'auto'
                    }}
                    data-testid={`plan-subscribe-${index}`}
                    onClick={() => handlePlanCheckout(plan)}
                  >
                    {plan.name.includes('Mensual') ? 'Elegir Plan Mensual' :
                     plan.name.includes('Semestral') ? 'Elegir Plan Semestral' :
                     plan.name.includes('Anual') ? 'Elegir Plan Anual' :
                     `Elegir ${plan.name.split(' - ')[0]}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#22C55E'}}>
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {content?.cta_final?.texts?.title ? content.cta_final.texts.title.replace(/^#+\s*/, '') : '¿Listo para proteger tu futuro?'}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{color: '#F0FDF4'}}>
            {content?.cta_final?.texts?.subtitle || 'Únete a miles de guatemaltecos que ya confían en ProtegeYA para su tranquilidad'}
          </p>
          <Button 
            size="lg" 
            className="bg-white px-8 py-4 text-lg rounded-full transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            style={{color: '#22C55E'}}
            data-testid="final-cta-button"
            onClick={() => {
              // Scroll to plans section
              document.querySelector('[data-testid="plans-section"]')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {content?.cta_final?.texts?.button_text || 'Comenzar Ahora - Sin Compromiso'}
          </Button>
        </div>
      </section>
    </div>
  );
};

const ComoFuncionaPage = () => (
  <div className="min-h-screen bg-gray-50 py-20">
    <div className="container mx-auto px-6">
      <h1 className="text-4xl font-bold text-center mb-16">¿Cómo Funciona?</h1>
      <div className="max-w-4xl mx-auto">
        <p className="text-lg text-gray-600 text-center mb-12">
          Descubre el proceso paso a paso para unirte a nuestra red de profesionales
        </p>
        {/* Content will be loaded from CMS */}
      </div>
    </div>
  </div>
);

const PlanesPage = () => {
  const [plans, setPlans] = useState([]);
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        setPlans(response.data);
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-6">
        <h1 className="text-4xl font-bold text-center mb-16">Planes y Precios</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card key={plan.id} className="relative">
              {plan.is_popular && (
                <Badge className="absolute top-4 right-4 bg-green-600" style={{backgroundColor: '#22C55E'}}>Popular</Badge>
              )}
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>Q{plan.price_q_monthly}/mes</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {plan.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-green-600" style={{color: '#22C55E'}}>✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button className="w-full">Elegir Plan</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const FAQPage = () => (
  <div className="min-h-screen bg-gray-50 py-20">
    <div className="container mx-auto px-6">
      <h1 className="text-4xl font-bold text-center mb-16">Preguntas Frecuentes</h1>
      <div className="max-w-3xl mx-auto">
        {/* FAQ content will be loaded from CMS */}
        <p className="text-center text-gray-600">Contenido de FAQ se cargará desde el CMS</p>
      </div>
    </div>
  </div>
);

const LegalPage = () => (
  <div className="min-h-screen bg-gray-50 py-20">
    <div className="container mx-auto px-6">
      <h1 className="text-4xl font-bold text-center mb-16">Términos Legales</h1>
      <div className="max-w-4xl mx-auto">
        {/* Legal content will be loaded from CMS */}
        <p className="text-center text-gray-600">Contenido legal se cargará desde el CMS</p>
      </div>
    </div>
  </div>
);

// Simple Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('admin@protegeya.gt');
  const [password, setPassword] = useState('admin123');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      console.log('Attempting login with:', email);
      const response = await axios.post(`${API}/admin/login`, {
        email,
        password
      });
      
      console.log('Login response:', response.data);
      localStorage.setItem('admin_token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      // Test the token immediately after login
      try {
        await axios.get(`${API}/admin/texts`);
        console.log('Token validated successfully');
        onLogin(true);
        toast.success('Acceso concedido');
      } catch (tokenError) {
        console.error('Token validation failed:', tokenError);
        console.log('Proceeding with login despite validation error (temporary fix)');
        // Temporary: proceed with login even if validation fails
        onLogin(true);
        toast.success('Acceso concedido (validación omitida)');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Credenciales inválidas');
      toast.error('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Contraseña</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
          required
        />
      </div>
      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}
      <button 
        type="submit" 
        disabled={loading}
        className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
      >
        {loading ? 'Verificando...' : 'Iniciar Sesión'}
      </button>
    </form>
  );
};

// Simple Admin Panel Component
// Admin Panel Component with separate content state
const AdminPanel = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [adminContent, setAdminContent] = useState({}); // Separate state for admin
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [media, setMedia] = useState([]);
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);
  const [editType, setEditType] = useState(null); // 'plan', 'media', 'user', 'text', etc.
  
  // Temporary settings for editing before saving
  const [tempSettings, setTempSettings] = useState({});
  const [settingsChanged, setSettingsChanged] = useState(false);
  
  // Test message state
  const [testMessage, setTestMessage] = useState({ phone: '', text: '' });

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  // Get hero video for admin panel
  const heroVideo = process.env.REACT_APP_HERO_VIDEO_URL ? 
    { url: process.env.REACT_APP_HERO_VIDEO_URL, title: "Video de Prueba" } : 
    adminContent?.hero?.videos?.[0];

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const cacheBuster = `?t=${Date.now()}`;
      const [contentRes, plansRes, settingsRes, usersRes, mediaRes, textsRes] = await Promise.all([
        axios.get(`${API}/content/home${cacheBuster}`, config),
        axios.get(`${API}/admin/plans${cacheBuster}`, config),
        axios.get(`${API}/admin/settings${cacheBuster}`, config),
        axios.get(`${API}/admin/users${cacheBuster}`, config),
        axios.get(`${API}/admin/media${cacheBuster}`, config),
        axios.get(`${API}/admin/texts${cacheBuster}`, config)
      ]);
      
      setAdminContent(contentRes.data);
      setPlans(plansRes.data);
      setSettings(settingsRes.data);
      setTempSettings(settingsRes.data); // Initialize temp settings
      setUsers(usersRes.data);
      setMedia(mediaRes.data);
      setTexts(textsRes.data);
      setSettingsChanged(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error cargando datos del panel');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Don't show toast here, let saveSettings handle it
      // toast.success('Configuración actualizada');
      // Don't fetchAllData here to avoid overriding tempSettings
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error; // Re-throw so saveSettings can handle the error
    }
  };

  // Handle temporary settings changes
  const updateTempSettings = (newSettings) => {
    setTempSettings(newSettings);
    setSettingsChanged(true);
  };

  const saveSettings = async () => {
    try {
      await updateSettings(tempSettings);
      // Update the main settings state with the saved temp settings
      setSettings(tempSettings);
      setSettingsChanged(false);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error guardando configuración');
    }
  };

  const resetSettings = () => {
    setTempSettings(settings);
    setSettingsChanged(false);
  };

  // Helper function to find existing text
  const findExistingText = (sectionKey, key) => {
    return texts.find(text => text.section_key === sectionKey && text.key === key);
  };

  // File upload helper
  const uploadFile = async (file, endpoint, additionalData = {}) => {
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await axios.post(`${API}/admin/${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  // Plan CRUD operations
  const createPlan = async (planData) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/plans`, planData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan creado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Error creando plan');
    }
  };

  const updatePlan = async (planId, planData) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/plans/${planId}`, planData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan actualizado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Error actualizando plan');
    }
  };

  const deletePlan = async (planId) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este plan?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan eliminado exitosamente');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error eliminando plan');
    }
  };

  // Media CRUD operations
  const createMediaWithFile = async (file, mediaData) => {
    try {
      const uploadData = await uploadFile(file, 'media/upload', mediaData);
      toast.success('Archivo subido exitosamente');
      fetchAllData();
      setEditModalOpen(false);
      return uploadData;
    } catch (error) {
      console.error('Error creating media:', error);
      toast.error('Error subiendo archivo');
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este archivo?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Archivo eliminado exitosamente');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Error eliminando archivo');
    }
  };

  const updateHeroVideo = async (videoUrl) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Encontrar el video con esta URL en la lista de media
      const selectedVideo = media.find(item => item.url === videoUrl);
      
      if (!selectedVideo) {
        toast.error('Video no encontrado');
        return;
      }
      
      // Si ya es el video del hero, no hacer nada
      if (selectedVideo.section_key === 'hero') {
        toast.info('Este video ya está configurado como video principal');
        return;
      }
      
      // Cambiar el section_key del video seleccionado a 'hero'
      await axios.put(`${API}/admin/media/${selectedVideo.id}`, {
        section_key: 'hero',
        title: selectedVideo.title,
        description: selectedVideo.description,
        order: selectedVideo.order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video del hero actualizado exitosamente');
      fetchAllData(); // Recargar datos
      
    } catch (error) {
      console.error('Error updating hero video:', error);
      toast.error('Error al actualizar video del hero');
    }
  };

  // UltraMSG test message function
  const sendTestMessage = async () => {
    if (!testMessage.phone || !testMessage.text) {
      toast.error('Por favor completa el número de teléfono y mensaje');
      return;
    }
    
    if (!settings.whatsapp?.ultramsg_instance_id || !settings.whatsapp?.ultramsg_token) {
      toast.error('Configura primero las credenciales de UltraMSG');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/test-ultramsg`, {
        phone: testMessage.phone,
        message: testMessage.text
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('✅ Mensaje enviado exitosamente!');
        setTestMessage({ phone: '', text: '' }); // Clear form
      } else {
        toast.error(`❌ Error enviando mensaje: ${response.data.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error enviando mensaje';
      toast.error(`❌ Error: ${errorMsg}`);
    }
  };

  // Text CRUD operations
  const createText = async (textData) => {
    console.log('createText called with:', textData);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/texts`, textData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Text create response:', response.data);
      toast.success('Texto creado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
      return response.data;
    } catch (error) {
      console.error('Error creating text:', error);
      toast.error('Error creando texto: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateText = async (textId, textData) => {
    console.log('updateText called with:', textId, textData);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.put(`${API}/admin/texts/${textId}`, textData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Text update response:', response.data);
      toast.success('Texto actualizado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
      return response.data;
    } catch (error) {
      console.error('Error updating text:', error);
      toast.error('Error actualizando texto: ' + (error.response?.data?.detail || error.message));
    }
  };

  // User CRUD operations
  const createUser = async (userData) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario creado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Error creando usuario');
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/users/${userId}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario actualizado exitosamente');
      fetchAllData();
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error actualizando usuario');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado exitosamente');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error eliminando usuario');
    }
  };

  // Logo upload
  const uploadLogo = async (file, height = 'h-24', width = 'w-auto') => {
    try {
      const uploadData = await uploadFile(file, 'upload/logo', { height, width });
      toast.success('Logo actualizado exitosamente');
      fetchAllData();
      return uploadData;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error subiendo logo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Panel Administrativo ProtegeYA</h1>
            <Button onClick={onLogout} variant="outline" className="text-red-600 border-red-300">
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="content">Contenido</TabsTrigger>
            <TabsTrigger value="plans">Planes</TabsTrigger>
            <TabsTrigger value="corredores">Corredores</TabsTrigger>
            <TabsTrigger value="settings">Configuración</TabsTrigger>
            <TabsTrigger value="integrations">Integraciones</TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
          </TabsList>

          {/* Content Management Tab */}
          <TabsContent value="content" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Hero Video Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Video Principal (Hero)</CardTitle>
                  <CardDescription>Gestiona el video de la sección principal</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Video Actual del Hero</Label>
                    {heroVideo ? (
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Fuente:</strong> {
                            heroVideo.url.includes('youtube.com') || heroVideo.url.includes('youtu.be') ? 'YouTube' :
                            heroVideo.url.includes('vimeo.com') ? 'Vimeo' :
                            heroVideo.url.includes('mdn.mozilla.net') ? 'Video Temporal (MDN)' : 
                            'Video Personalizado'
                          }
                        </p>
                        {heroVideo.url.includes('youtube.com') || heroVideo.url.includes('youtu.be') ? (
                          <div className="aspect-video">
                            <iframe
                              src={heroVideo.url.includes('/embed/') ? heroVideo.url : heroVideo.url.includes('watch?v=') ? heroVideo.url.replace('watch?v=', 'embed/') : heroVideo.url.replace('youtu.be/', 'youtube.com/embed/')}
                              className="w-full h-full rounded"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Preview del video hero"
                            ></iframe>
                          </div>
                        ) : (
                          <video 
                            src={heroVideo.url} 
                            controls 
                            className="w-full h-32 object-cover rounded" 
                            muted
                          />
                        )}
                        <p className="text-xs text-gray-500 mt-2 truncate">{heroVideo.url}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500">No hay video configurado</p>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <Label>Cambiar Video del Hero</Label>
                    
                    {/* Opción 1: Seleccionar video subido */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Opción 1: Seleccionar video subido</label>
                      <div className="flex gap-2">
                        <select 
                          className="flex-1 px-3 py-2 border rounded-md"
                          onChange={(e) => {
                            if (e.target.value) {
                              // Actualizar video del hero
                              updateHeroVideo(e.target.value);
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">Seleccionar video subido...</option>
                          {media.filter(file => file.type === 'video').map(video => (
                            <option key={video.id} value={video.url}>
                              {video.filename || `Video ${video.id.substring(0, 8)}`}
                            </option>
                          ))}
                        </select>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            // Create file input for video upload
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'video/*';
                            input.onchange = async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              try {
                                // Verificar tamaño del archivo (100MB máximo)
                                const maxSize = 100 * 1024 * 1024; // 100MB
                                if (file.size > maxSize) {
                                  toast.error(`Video demasiado grande. Máximo 100MB. Tu video: ${(file.size / (1024*1024)).toFixed(1)}MB`);
                                  return;
                                }
                                
                                // Verificar token antes de subir
                                const token = localStorage.getItem('admin_token');
                                if (!token) {
                                  toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
                                  return;
                                }
                                
                                // Configurar axios defaults
                                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('section_key', 'hero');
                                formData.append('title', file.name || 'Video Principal');
                                
                                toast.info(`Subiendo video (${(file.size / (1024*1024)).toFixed(1)}MB)... Por favor espera.`);
                                
                                const response = await axios.post(`${API}/admin/media/upload`, formData, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  },
                                  timeout: 120000, // 120 segundos (2 minutos) para videos grandes
                                  onUploadProgress: (progressEvent) => {
                                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                    console.log(`Upload progress: ${percentCompleted}%`);
                                  }
                                });
                                
                                console.log('Upload response:', response.data);
                                
                                if (response.data.success || response.data.file) {
                                  const fileUrl = response.data.file?.url || response.data.url;
                                  toast.success('Video subido exitosamente');
                                  
                                  // Update hero video automatically if we have the URL
                                  if (fileUrl) {
                                    await updateHeroVideo(fileUrl);
                                  }
                                  
                                  fetchAllData(); // Refresh data
                                } else {
                                  toast.error('Error en respuesta del servidor');
                                }
                              } catch (error) {
                                console.error('Error uploading video:', error);
                                
                                if (error.response?.status === 401) {
                                  toast.error('Sesión expirada. Recarga la página e inicia sesión nuevamente.');
                                  // Clear invalid token
                                  localStorage.removeItem('admin_token');
                                  delete axios.defaults.headers.common['Authorization'];
                                } else if (error.response?.status === 413) {
                                  toast.error('Video demasiado grande. Máximo 100MB permitido.');
                                } else if (error.response?.status === 502) {
                                  toast.error('Error del servidor (502). El video puede ser demasiado grande o el servidor está ocupado. Intenta con un video más pequeño.');
                                } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                                  toast.error('Tiempo de espera agotado. El video es muy grande. Por favor, usa un archivo más pequeño.');
                                } else {
                                  const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
                                  toast.error('Error subiendo video: ' + errorMsg);
                                }
                              }
                            }
                          };
                          input.click();
                        }}
                      >
                        📹 Subir Nuevo Video
                      </Button>
                      </div>
                    </div>
                    
                    {/* Opción 2: URL de YouTube */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Opción 2: URL de YouTube o Video Externo</label>
                      <div className="flex gap-2">
                        <input 
                          type="text"
                          placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
                          className="flex-1 px-3 py-2 border rounded-md"
                          id="youtube-url-input"
                        />
                        <Button 
                          variant="outline"
                          onClick={async () => {
                            const input = document.getElementById('youtube-url-input');
                            const url = input.value.trim();
                            
                            if (!url) {
                              toast.error('Por favor ingresa una URL');
                              return;
                            }
                            
                            // Validar que sea una URL válida
                            try {
                              new URL(url);
                            } catch {
                              toast.error('URL inválida. Ingresa una URL completa.');
                              return;
                            }
                            
                            try {
                              const token = localStorage.getItem('admin_token');
                              if (!token) {
                                toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
                                return;
                              }
                              
                              toast.info('Configurando video externo...');
                              
                              // Crear o actualizar media con URL externa
                              const response = await axios.post(`${API}/admin/media/external`, {
                                url: url,
                                type: 'video',
                                section_key: 'hero',
                                title: 'Video de YouTube/Externo'
                              }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              
                              if (response.data.success) {
                                toast.success('✅ Video de YouTube configurado exitosamente');
                                input.value = ''; // Limpiar input
                                fetchAllData(); // Recargar datos
                              } else {
                                toast.error('Error al configurar el video');
                              }
                            } catch (error) {
                              console.error('Error setting external video:', error);
                              toast.error('Error: ' + (error.response?.data?.detail || error.message));
                            }
                          }}
                        >
                          🔗 Usar esta URL
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        💡 Copia la URL completa del video de YouTube (ej: https://www.youtube.com/watch?v=abc123) o cualquier video externo
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Media Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Gestión de Archivos</CardTitle>
                  <CardDescription>Sube y administra imágenes y videos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    onClick={() => {
                      setEditType('media');
                      setCurrentEdit(null);
                      setEditModalOpen(true);
                    }}
                    className="w-full" 
                    style={{backgroundColor: '#22C55E'}}
                  >
                    Subir Nuevo Archivo
                  </Button>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {media.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                            {item.type === 'image' ? '🖼️' : '🎥'}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{item.title}</p>
                            <p className="text-xs text-gray-500">{item.section_key}</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600"
                          onClick={() => deleteMedia(item.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Text Blocks */}
              <Card>
                <CardHeader>
                  <CardTitle>Gestión de Textos</CardTitle>
                  <CardDescription>Edita el contenido de texto de la página</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="border rounded p-3">
                      <h4 className="font-medium">Sección Hero - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.hero?.texts?.title ? 
                          adminContent.hero.texts.title.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('hero', 'title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'hero',
                            key: 'title',
                            content_markdown: adminContent.hero?.texts?.title || '',
                            type: 'title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>
                    
                    <div className="border rounded p-3">
                      <h4 className="font-medium">Hero - Subtítulo</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.hero?.texts?.subtitle ? 
                          adminContent.hero.texts.subtitle.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('hero', 'subtitle');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'hero',
                            key: 'subtitle',
                            content_markdown: adminContent.hero?.texts?.subtitle || '',
                            type: 'subtitle'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">Hero - Botón Principal</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.hero?.texts?.cta_primary || 'No configurado (Protégete Ahora)'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('hero', 'cta_primary');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'hero',
                            key: 'cta_primary',
                            content_markdown: adminContent.hero?.texts?.cta_primary || 'Protégete Ahora',
                            type: 'button'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">Hero - Botón WhatsApp</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.hero?.texts?.cta_secondary || 'No configurado (Consultar por WhatsApp)'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('hero', 'cta_secondary');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'hero',
                            key: 'cta_secondary',
                            content_markdown: adminContent.hero?.texts?.cta_secondary || 'Consultar por WhatsApp',
                            type: 'button'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.title || 'No configurado'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'title',
                            content_markdown: adminContent.como_funciona?.texts?.title || '',
                            type: 'title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Subtítulo</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.subtitle ? 
                          adminContent.como_funciona.texts.subtitle.substring(0, 50) + '...' : 
                          'No configurado (Este es el texto: "Un proceso simple y seguro...")'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'subtitle');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'subtitle',
                            content_markdown: adminContent.como_funciona?.texts?.subtitle || 'Un proceso simple y seguro diseñado para brindarte la mejor protección',
                            type: 'subtitle'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 1 - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step1_title || 'No configurado'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step1_title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step1_title',
                            content_markdown: adminContent.como_funciona?.texts?.step1_title || 'Elige tu Protección',
                            type: 'step1_title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 1 - Descripción</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step1_description ? 
                          adminContent.como_funciona.texts.step1_description.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step1_description');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step1_description',
                            content_markdown: adminContent.como_funciona?.texts?.step1_description || 'Selecciona el plan de seguro que mejor se adapte a tus necesidades',
                            type: 'step1_description'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 2 - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step2_title || 'No configurado'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step2_title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step2_title',
                            content_markdown: adminContent.como_funciona?.texts?.step2_title || 'Proceso Rápido',
                            type: 'step2_title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 2 - Descripción</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step2_description ? 
                          adminContent.como_funciona.texts.step2_description.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step2_description');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step2_description',
                            content_markdown: adminContent.como_funciona?.texts?.step2_description || 'Completa tu solicitud en línea de forma sencilla y segura',
                            type: 'step2_description'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 3 - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step3_title || 'No configurado'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step3_title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step3_title',
                            content_markdown: adminContent.como_funciona?.texts?.step3_title || 'Protección Inmediata',
                            type: 'step3_title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">¿Cómo Funciona? - Paso 3 - Descripción</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.como_funciona?.texts?.step3_description ? 
                          adminContent.como_funciona.texts.step3_description.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('como_funciona', 'step3_description');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'como_funciona',
                            key: 'step3_description',
                            content_markdown: adminContent.como_funciona?.texts?.step3_description || 'Obtén tu cobertura y disfruta de la tranquilidad que mereces',
                            type: 'step3_description'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">Planes - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.planes?.texts?.title || 'No configurado'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('planes', 'title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'planes',
                            key: 'title',
                            content_markdown: adminContent.planes?.texts?.title || '## Planes de Protección ProtegeYA',
                            type: 'title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">Planes - Subtítulo</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.planes?.texts?.subtitle ? 
                          adminContent.planes.texts.subtitle.substring(0, 50) + '...' : 
                          'No configurado'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('planes', 'subtitle');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'planes',
                            key: 'subtitle',
                            content_markdown: adminContent.planes?.texts?.subtitle || 'Encuentra la protección perfecta para ti y tu familia',
                            type: 'subtitle'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">CTA Final - Título</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.cta_final?.texts?.title || 'No configurado (¿Listo para proteger tu futuro?)'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('cta_final', 'title');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'cta_final',
                            key: 'title',
                            content_markdown: adminContent.cta_final?.texts?.title || '## ¿Listo para proteger tu futuro?',
                            type: 'title'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">CTA Final - Subtítulo</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.cta_final?.texts?.subtitle ? 
                          adminContent.cta_final.texts.subtitle.substring(0, 50) + '...' : 
                          'No configurado (Únete a miles de guatemaltecos...)'
                        }
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('cta_final', 'subtitle');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'cta_final',
                            key: 'subtitle',
                            content_markdown: adminContent.cta_final?.texts?.subtitle || 'Únete a miles de guatemaltecos que ya confían en ProtegeYA para su tranquilidad',
                            type: 'subtitle'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="border rounded p-3">
                      <h4 className="font-medium">CTA Final - Texto del Botón</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminContent.cta_final?.texts?.button_text || 'No configurado (Comenzar Ahora - Sin Compromiso)'}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => {
                          const existingText = findExistingText('cta_final', 'button_text');
                          setEditType('text');
                          setCurrentEdit(existingText || {
                            section_key: 'cta_final',
                            key: 'button_text',
                            content_markdown: adminContent.cta_final?.texts?.button_text || 'Comenzar Ahora - Sin Compromiso',
                            type: 'button_text'
                          });
                          setEditModalOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Corredores Tab */}
          <TabsContent value="corredores" className="space-y-6">
            <CorredoresTab />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Save/Reset Buttons */}
            {settingsChanged && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-yellow-700">⚠️ Tienes cambios sin guardar</span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={resetSettings} 
                      variant="outline"
                      size="sm"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={saveSettings}
                      size="sm"
                      style={{backgroundColor: '#22C55E'}}
                    >
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Logo Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuración de Logo</CardTitle>
                  <CardDescription>Administra el logo de la empresa</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Logo Actual</Label>
                    <div className="flex items-center gap-4">
                      {tempSettings.logo?.url && (
                        <img 
                          src={tempSettings.logo.url} 
                          alt="Logo actual" 
                          className="h-16 w-auto border rounded bg-white p-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              uploadLogo(e.target.files[0], tempSettings.logo?.height || 'h-24', tempSettings.logo?.width || 'w-auto');
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Formatos: JPG, PNG, GIF, WebP
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Alto</Label>
                      <select
                        value={tempSettings.logo?.height || 'h-24'}
                        onChange={(e) => {
                          const newSettings = {
                            ...tempSettings,
                            logo: { ...tempSettings.logo, height: e.target.value }
                          };
                          updateTempSettings(newSettings);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="h-12">Pequeño (h-12)</option>
                        <option value="h-16">Mediano (h-16)</option>
                        <option value="h-20">Grande (h-20)</option>
                        <option value="h-24">Extra Grande (h-24)</option>
                        <option value="h-32">Enorme (h-32)</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Ancho</Label>
                      <select
                        value={tempSettings.logo?.width || 'w-auto'}
                        onChange={(e) => {
                          const newSettings = {
                            ...tempSettings,
                            logo: { ...tempSettings.logo, width: e.target.value }
                          };
                          updateTempSettings(newSettings);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="w-auto">Automático</option>
                        <option value="w-32">Fijo Pequeño</option>
                        <option value="w-48">Fijo Mediano</option>
                        <option value="w-64">Fijo Grande</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Landing Sections Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuración de Secciones</CardTitle>
                  <CardDescription>Habilita/deshabilita secciones de la landing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tempSettings.landing_sections && Object.entries(tempSettings.landing_sections).map(([key, section]) => (
                    <div key={key} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <h4 className="font-medium capitalize">{key.replace('_', ' ')}</h4>
                        <p className="text-sm text-gray-500">{section.title || 'Configurar título...'}</p>
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={section.enabled || false}
                          onChange={(e) => {
                            const newSettings = {
                              ...tempSettings,
                              landing_sections: {
                                ...tempSettings.landing_sections,
                                [key]: { ...section, enabled: e.target.checked }
                              }
                            };
                            updateTempSettings(newSettings);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Habilitado</span>
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Información de Contacto</CardTitle>
                  <CardDescription>Datos de contacto de la empresa</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Número de WhatsApp</Label>
                    <Input
                      value={tempSettings.contact_info?.whatsapp_number || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          contact_info: { ...tempSettings.contact_info, whatsapp_number: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="50212345678"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={tempSettings.contact_info?.email || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          contact_info: { ...tempSettings.contact_info, email: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="info@protegeya.gt"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input
                      value={tempSettings.contact_info?.address || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          contact_info: { ...tempSettings.contact_info, address: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="Guatemala, Guatemala"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Horarios de Atención</Label>
                    <Input
                      value={tempSettings.contact_info?.business_hours || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          contact_info: { ...tempSettings.contact_info, business_hours: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="Lunes a Viernes 8:00 AM - 6:00 PM"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* SEO Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuración SEO</CardTitle>
                  <CardDescription>Metadatos para motores de búsqueda</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Título del Sitio</Label>
                    <Input
                      value={tempSettings.seo?.site_title || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          seo: { ...tempSettings.seo, site_title: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="ProtegeYA - Seguros en Guatemala"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Meta Descripción</Label>
                    <Textarea
                      value={tempSettings.seo?.meta_description || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          seo: { ...tempSettings.seo, meta_description: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="La plataforma de seguros más confiable de Guatemala..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Palabras Clave</Label>
                    <Input
                      value={tempSettings.seo?.meta_keywords || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          seo: { ...tempSettings.seo, meta_keywords: e.target.value }
                        };
                        updateTempSettings(newSettings);
                      }}
                      placeholder="seguros guatemala, protección, seguros de vida"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Integrations Tab - Enhanced */}
          <TabsContent value="integrations" className="space-y-6">
            {/* OpenAI Integration */}
            <Card>
              <CardHeader>
                <CardTitle>Integración OpenAI</CardTitle>
                <CardDescription>Configuración del asistente de IA para WhatsApp</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* API Key Selection */}
                <div className="space-y-3">
                  <Label>Configuración de API Key</Label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input 
                        type="radio" 
                        name="api_key_type"
                        checked={settings.openai?.use_emergent_key !== false}
                        onChange={() => {
                          const newSettings = {
                            ...settings,
                            openai: { ...settings.openai, use_emergent_key: true }
                          };
                          updateSettings(newSettings);
                        }}
                      />
                      <span className="text-sm font-medium">Usar Emergent LLM Key (Recomendado)</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">
                      Usa créditos de tu cuenta Emergent. Fácil configuración, sin necesidad de API keys externas.
                    </p>
                    
                    <label className="flex items-center space-x-2">
                      <input 
                        type="radio" 
                        name="api_key_type"
                        checked={settings.openai?.use_emergent_key === false}
                        onChange={() => {
                          const newSettings = {
                            ...settings,
                            openai: { ...settings.openai, use_emergent_key: false }
                          };
                          updateSettings(newSettings);
                        }}
                      />
                      <span className="text-sm font-medium">Usar mi propia API Key de OpenAI</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">
                      Usa tu propia cuenta de OpenAI. Requiere configuración adicional.
                    </p>
                  </div>
                </div>

                {/* User API Key Input */}
                {settings.openai?.use_emergent_key === false && (
                  <div className="space-y-2">
                    <Label>Tu API Key de OpenAI</Label>
                    <Input 
                      type="password"
                      placeholder="sk-..." 
                      value={settings.openai?.user_openai_api_key || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...settings,
                          openai: { ...settings.openai, user_openai_api_key: e.target.value }
                        };
                        updateSettings(newSettings);
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Obtén tu API key en: <a href="https://platform.openai.com/api-keys" target="_blank" className="text-blue-600 underline">platform.openai.com/api-keys</a>
                    </p>
                  </div>
                )}

                {/* AI WhatsApp Toggle */}
                <div className="space-y-3">
                  <Label>Estado de la IA de WhatsApp</Label>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${settings.openai?.ai_enabled === true ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div>
                        <p className="font-medium">
                          {settings.openai?.ai_enabled === true ? '🤖 IA Conectada' : '🔌 IA Desconectada'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {settings.openai?.ai_enabled === true
                            ? 'La IA responderá automáticamente a mensajes de WhatsApp' 
                            : 'Los mensajes de WhatsApp no serán procesados por IA'
                          }
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const currentlyEnabled = settings.openai?.ai_enabled !== false;
                        const newSettings = {
                          ...settings,
                          openai: { 
                            ...settings.openai, 
                            ai_enabled: !currentlyEnabled
                          }
                        };
                        updateSettings(newSettings);
                        toast.success(newSettings.openai.ai_enabled ? 'IA de WhatsApp Conectada' : 'IA de WhatsApp Desconectada');
                      }}
                      style={{
                        backgroundColor: settings.openai?.ai_enabled === true ? '#FEE2E2' : '#F0FDF4',
                        color: settings.openai?.ai_enabled === true ? '#DC2626' : '#16A34A',
                        borderColor: settings.openai?.ai_enabled === true ? '#FCA5A5' : '#BBF7D0'
                      }}
                    >
                      {settings.openai?.ai_enabled === true ? 'Desconectar IA' : 'Conectar IA'}
                    </Button>
                  </div>
                </div>

                {/* Assistant Prompt - ALWAYS EDITABLE */}
                <div className="space-y-2">
                  <Label>Prompt del Asistente <span className="text-green-600">✨ Siempre Editable</span></Label>
                  <Textarea 
                    key={`prompt-${tempSettings.openai?.assistant_prompt?.substring(0, 20) || settings.openai?.assistant_prompt?.substring(0, 20) || 'empty'}`}
                    placeholder="Instrucciones para el asistente de IA en WhatsApp..."
                    defaultValue={tempSettings.openai?.assistant_prompt || settings.openai?.assistant_prompt || ''}
                    onChange={(e) => {
                      console.log('Prompt changed:', e.target.value.length, 'characters');
                      const newSettings = {
                        ...tempSettings,
                        openai: { ...tempSettings.openai, assistant_prompt: e.target.value }
                      };
                      updateTempSettings(newSettings);
                    }}
                    onFocus={() => console.log('Prompt field focused')}
                    rows={6}
                    disabled={false}
                    readOnly={false}
                    autoComplete="off"
                    spellCheck={true}
                    className="min-h-[120px] resize-y"
                  />
                  <div className="flex justify-between items-center text-xs">
                    <p className="text-gray-500">
                      Define cómo debe comportarse el asistente de IA en WhatsApp. 
                      {settings.openai?.ai_enabled ? '🟢 IA Activa' : '🔴 IA Inactiva'}
                    </p>
                    <p className="text-gray-400">
                      {settings.openai?.assistant_prompt?.length || 0} caracteres
                    </p>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const defaultPrompt = "Eres un asistente de atención al cliente para ProtegeYA, la plataforma de seguros líder en Guatemala. Ayuda a los clientes con información sobre nuestros planes de protección, coberturas, precios y beneficios. Nuestros planes son: Protección Básica (Q199/mes), Protección Completa (Q399/mes) y Protección Premium (Q699/mes). Nuestro sitio web es protegeyagt.com donde los clientes pueden ver más información y registrarse como corredores. Siempre responde en español guatemalteco y sé amable, profesional y empático.";
                        const newSettings = {
                          ...settings,
                          openai: { ...settings.openai, assistant_prompt: defaultPrompt }
                        };
                        updateSettings(newSettings);
                        toast.success('Prompt restaurado al predeterminado');
                      }}
                    >
                      🔄 Restaurar Default
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const newSettings = {
                          ...settings,
                          openai: { ...settings.openai, assistant_prompt: '' }
                        };
                        updateSettings(newSettings);
                        toast.success('Prompt limpiado');
                      }}
                    >
                      🗑️ Limpiar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recurrente Integration - Enhanced */}
            <RecurrenteIntegrationCard 
              settings={settings}
              updateSettings={updateSettings}
              plans={plans}
            />

            {/* UltraMSG Integration - Now Fully Functional */}
            <Card>
              <CardHeader>
                <CardTitle>Integración UltraMSG</CardTitle>
                <CardDescription>Configuración para notificaciones de WhatsApp</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    ⚠️ <strong>Campos Editables:</strong> Si no puedes escribir, recarga la página con Ctrl+Shift+R
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Forzar clear de los campos
                      const newSettings = {
                        ...settings,
                        whatsapp: {
                          ...settings.whatsapp,
                          ultramsg_instance_id: '',
                          ultramsg_token: '',
                          ultramsg_webhook_secret: ''
                        }
                      };
                      updateSettings(newSettings);
                      toast.success('Campos limpiados para edición');
                    }}
                    className="mt-2"
                  >
                    🔄 Limpiar Campos
                  </Button>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Instance ID</Label>
                    <Input 
                      key={`instance-${Math.random()}`}
                      placeholder="Ej: instance108171"
                      value={settings.whatsapp?.ultramsg_instance_id || ''}
                      onChange={(e) => {
                        console.log('Instance ID changed:', e.target.value);
                        const newSettings = {
                          ...settings,
                          whatsapp: { ...settings.whatsapp, ultramsg_instance_id: e.target.value }
                        };
                        updateSettings(newSettings);
                      }}
                      onFocus={() => console.log('Instance ID focused')}
                      onClick={() => console.log('Instance ID clicked')}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Token</Label>
                    <Input 
                      key={`token-${Math.random()}`}
                      type="text"
                      placeholder="Tu token de UltraMSG"
                      value={settings.whatsapp?.ultramsg_token || ''}
                      onChange={(e) => {
                        console.log('Token changed:', e.target.value);
                        const newSettings = {
                          ...settings,
                          whatsapp: { ...settings.whatsapp, ultramsg_token: e.target.value }
                        };
                        updateSettings(newSettings);
                      }}
                      onFocus={() => console.log('Token focused')}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Webhook Secret</Label>
                    <Input 
                      key={`webhook-${Math.random()}`}
                      type="text"
                      placeholder="Secret para validación de webhooks"
                      value={settings.whatsapp?.ultramsg_webhook_secret || ''}
                      onChange={(e) => {
                        console.log('Webhook secret changed:', e.target.value);
                        const newSettings = {
                          ...settings,
                          whatsapp: { ...settings.whatsapp, ultramsg_webhook_secret: e.target.value }
                        };
                        updateSettings(newSettings);
                      }}
                      onFocus={() => console.log('Webhook secret focused')}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Teléfono del Administrador</Label>
                    <Input 
                      key={`phone-${Date.now()}`}
                      placeholder="50212345678 (formato internacional)"
                      value={settings.whatsapp?.admin_phone_e164 || ''}
                      readOnly={false}
                      disabled={false}
                      onChange={(e) => {
                        const newSettings = {
                          ...settings,
                          whatsapp: { ...settings.whatsapp, admin_phone_e164: e.target.value }
                        };
                        updateSettings(newSettings);
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Número donde recibirás notificaciones de nuevas suscripciones
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input 
                        type="checkbox" 
                        checked={settings.notifications?.notify_whatsapp_on_subscription || false}
                        onChange={(e) => {
                          const newSettings = {
                            ...settings,
                            notifications: { 
                              ...settings.notifications, 
                              notify_whatsapp_on_subscription: e.target.checked 
                            }
                          };
                          updateSettings(newSettings);
                        }}
                      />
                      <span className="text-sm">Notificar por WhatsApp nueva suscripción</span>
                    </label>
                  </div>

                  <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded">
                    <p className="text-sm text-green-700">
                      ✅ <strong>Integración Funcional:</strong> Las notificaciones se enviarán automáticamente cuando configures las credenciales.
                    </p>
                  </div>

                  {/* Webhook URL Information */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">🔗 Configuración de Webhook en UltraMSG</h4>
                    <p className="text-sm text-blue-700 mb-2">
                      Configura este webhook URL en tu panel de UltraMSG:
                    </p>
                    <div className="bg-blue-100 p-2 rounded border font-mono text-sm text-blue-900 flex items-center justify-between">
                      <span>{BACKEND_URL}/api/webhooks/ultramsg</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`${BACKEND_URL}/api/webhooks/ultramsg`);
                          toast.success('URL copiada al portapapeles');
                        }}
                      >
                        Copiar
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      📋 Paso a paso: Panel UltraMSG → Settings → Webhooks → Webhook URL → Pegar la URL de arriba
                    </p>
                  </div>

                  {/* Test Message Section */}
                  <div className="border-t pt-4 mt-6">
                    <h4 className="font-semibold mb-3">🧪 Enviar Mensaje de Prueba</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Número de Teléfono</Label>
                          <Input 
                            placeholder="50212345678"
                            value={testMessage.phone || ''}
                            onChange={(e) => setTestMessage({...testMessage, phone: e.target.value})}
                          />
                          <p className="text-xs text-gray-500">Formato internacional sin + (ej: 50212345678)</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Mensaje</Label>
                          <Input 
                            placeholder="Hola! Este es un mensaje de prueba..."
                            value={testMessage.text || ''}
                            onChange={(e) => setTestMessage({...testMessage, text: e.target.value})}
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={sendTestMessage}
                        disabled={!testMessage.phone || !testMessage.text || !settings.whatsapp?.ultramsg_instance_id || !settings.whatsapp?.ultramsg_token}
                        style={{backgroundColor: '#22C55E'}}
                        className="w-full md:w-auto"
                      >
                        📱 Enviar Mensaje de Prueba
                      </Button>
                      {(!settings.whatsapp?.ultramsg_instance_id || !settings.whatsapp?.ultramsg_token) && (
                        <p className="text-xs text-amber-600">
                          ⚠️ Configura primero el Instance ID y Token para poder enviar mensajes de prueba
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Human Takeover Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>⚙️ Configuración de Detección de Humano</CardTitle>
                <p className="text-sm text-gray-500">
                  Configura la detección automática cuando un operador humano responde en WhatsApp
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="human-takeover-enabled"
                      checked={tempSettings.whatsapp?.human_takeover_enabled ?? settings.whatsapp?.human_takeover_enabled ?? true}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          whatsapp: { 
                            ...tempSettings.whatsapp, 
                            human_takeover_enabled: e.target.checked 
                          }
                        };
                        updateTempSettings(newSettings);
                      }}
                      className="rounded"
                    />
                    <Label htmlFor="human-takeover-enabled">
                      Activar detección automática de humano
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Número del Operador (Formato E164)</Label>
                    <Input 
                      key={`admin-phone-${tempSettings.whatsapp?.admin_phone_e164 || settings.whatsapp?.admin_phone_e164 || 'empty'}`}
                      placeholder="Ej: 50243821122 (sin +, sin espacios, sin guiones)"
                      defaultValue={tempSettings.whatsapp?.admin_phone_e164 || settings.whatsapp?.admin_phone_e164 || ''}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          whatsapp: { 
                            ...tempSettings.whatsapp, 
                            admin_phone_e164: e.target.value.replace(/[^0-9]/g, '')
                          }
                        };
                        updateTempSettings(newSettings);
                      }}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <p className="text-xs text-gray-500">
                      💡 Cuando un mensaje venga de este número, el bot se desactivará automáticamente
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Tiempo de Reactivación del Bot (minutos)</Label>
                    <Input 
                      key={`timeout-${tempSettings.whatsapp?.bot_reactivation_timeout_minutes || settings.whatsapp?.bot_reactivation_timeout_minutes || 30}`}
                      type="number"
                      min="1"
                      max="1440"
                      placeholder="30"
                      defaultValue={tempSettings.whatsapp?.bot_reactivation_timeout_minutes || settings.whatsapp?.bot_reactivation_timeout_minutes || 30}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          whatsapp: { 
                            ...tempSettings.whatsapp, 
                            bot_reactivation_timeout_minutes: parseInt(e.target.value) || 30
                          }
                        };
                        updateTempSettings(newSettings);
                      }}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500">
                      ⏰ Si el humano no responde durante este tiempo, el bot se reactivará automáticamente
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>⚡ Comandos para Tomar Control (separados por comas)</Label>
                    <Input 
                      key={`takeover-cmd-${tempSettings.whatsapp?.command_human_takeover || settings.whatsapp?.command_human_takeover || 'empty'}`}
                      placeholder="#HUMANO, HUMANO, #TAKE"
                      defaultValue={tempSettings.whatsapp?.command_human_takeover || settings.whatsapp?.command_human_takeover || '#HUMANO, HUMANO, #TAKE'}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          whatsapp: { 
                            ...tempSettings.whatsapp, 
                            command_human_takeover: e.target.value
                          }
                        };
                        updateTempSettings(newSettings);
                      }}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500">
                      💡 Cuando un cliente envíe cualquiera de estos comandos, el bot se desactivará y esperará a que tú tomes el control
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>🤖 Comandos para Reactivar Bot (separados por comas)</Label>
                    <Input 
                      key={`release-cmd-${tempSettings.whatsapp?.command_bot_release || settings.whatsapp?.command_bot_release || 'empty'}`}
                      placeholder="#BOT, BOT, #RELEASE"
                      defaultValue={tempSettings.whatsapp?.command_bot_release || settings.whatsapp?.command_bot_release || '#BOT, BOT, #RELEASE'}
                      onChange={(e) => {
                        const newSettings = {
                          ...tempSettings,
                          whatsapp: { 
                            ...tempSettings.whatsapp, 
                            command_bot_release: e.target.value
                          }
                        };
                        updateTempSettings(newSettings);
                      }}
                      readOnly={false}
                      disabled={false}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500">
                      💡 Cuando un cliente envíe cualquiera de estos comandos, el bot se reactivará automáticamente
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 Cómo Funciona con Comandos</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>1️⃣ Cliente envía mensaje → Bot responde automáticamente</li>
                      <li>2️⃣ Cliente envía comando (ej: HUMANO) → Bot se desactiva</li>
                      <li>3️⃣ Recibes notificación: "Modo manual activado"</li>
                      <li>4️⃣ Tú respondes manualmente desde WhatsApp</li>
                      <li>5️⃣ Cliente envía comando (ej: BOT) → Bot se reactiva</li>
                      <li>6️⃣ O esperas {tempSettings.whatsapp?.bot_reactivation_timeout_minutes || settings.whatsapp?.bot_reactivation_timeout_minutes || 30} minutos → Bot se reactiva automáticamente</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-2">⚠️ Importante</h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li>• El número debe estar en formato E164 (solo dígitos)</li>
                      <li>• Ejemplo: 50243821122 (código país + número)</li>
                      <li>• UltraMSG debe enviar webhooks de TODOS los mensajes (incluso los tuyos)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Changes Button */}
            {settingsChanged && (
              <div className="fixed bottom-6 right-6 bg-white shadow-lg rounded-lg p-4 border-2 border-green-500 z-50">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Tienes cambios sin guardar</p>
                    <p className="text-sm text-gray-500">Haz click en "Guardar Cambios" para aplicarlos</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setTempSettings(settings);
                        setSettingsChanged(false);
                      }}
                      size="sm"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={saveSettings}
                      size="sm"
                      style={{backgroundColor: '#22C55E'}}
                    >
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Users Management */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Gestión de Usuarios Admin</CardTitle>
                    <CardDescription>Administra las cuentas de administrador</CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditType('user');
                      setCurrentEdit(null);
                      setEditModalOpen(true);
                    }}
                    style={{backgroundColor: '#22C55E'}}
                  >
                    Agregar Nuevo Usuario
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="border rounded p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{user.email}</h4>
                          <p className="text-sm text-gray-600">
                            Roles: {user.roles?.join(', ') || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Creado: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditType('user');
                              setCurrentEdit(user);
                              setEditModalOpen(true);
                            }}
                          >
                            Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={() => deleteUser(user.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Continue with the rest in the next part... */}
          <TabsContent value="plans" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Gestión de Planes</CardTitle>
                    <CardDescription>Administra los planes de protección</CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditType('plan');
                      setCurrentEdit(null);
                      setEditModalOpen(true);
                    }}
                    style={{backgroundColor: '#22C55E'}}
                  >
                    Crear Nuevo Plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {plans.map((plan) => (
                    <div key={plan.id} className="border rounded p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-lg">{plan.name}</h4>
                            {plan.is_popular && (
                              <Badge style={{backgroundColor: '#22C55E'}}>Popular</Badge>
                            )}
                          </div>
                          <p className="text-xl font-bold text-green-600 mt-1">Q{plan.price_q_monthly}/mes</p>
                          <div className="mt-2">
                            <p className="text-sm font-medium mb-1">Beneficios:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                              {plan.benefits.slice(0, 4).map((benefit, idx) => (
                                <span key={idx} className="text-sm text-gray-600">• {benefit}</span>
                              ))}
                              {plan.benefits.length > 4 && (
                                <span className="text-sm text-gray-500">... y {plan.benefits.length - 4} más</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditType('plan');
                              setCurrentEdit(plan);
                              setEditModalOpen(true);
                            }}
                          >
                            Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={() => deletePlan(plan.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Continue with the rest in the next part... */}
        </Tabs>
      </div>

      {/* Edit Modal */}
      <EditModal 
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        editType={editType}
        currentEdit={currentEdit}
        onSave={(data) => {
          console.log('onSave called with editType:', editType, 'data:', data);
          
          if (editType === 'plan') {
            if (currentEdit) {
              updatePlan(currentEdit.id, data);
            } else {
              createPlan(data);
            }
          } else if (editType === 'media') {
            createMediaWithFile(data.file, data);
          } else if (editType === 'text') {
            if (currentEdit && currentEdit.id) {
              console.log('Updating existing text with ID:', currentEdit.id);
              // Update existing text
              updateText(currentEdit.id, data);
            } else {
              console.log('Creating new text');
              // Create new text
              createText(data);
            }
          } else if (editType === 'user') {
            if (currentEdit) {
              updateUser(currentEdit.id, data);
            } else {
              createUser(data);
            }
          }
        }}
      />
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ open, onClose, editType, currentEdit, onSave }) => {
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (open) {
      if (editType === 'plan' && currentEdit) {
        setFormData({
          name: currentEdit.name || '',
          price_q_monthly: currentEdit.price_q_monthly || 0,
          benefits: currentEdit.benefits || [''],
          is_popular: currentEdit.is_popular || false
        });
      } else if (editType === 'plan') {
        setFormData({
          name: '',
          price_q_monthly: 0,
          benefits: [''],
          is_popular: false
        });
      } else if (editType === 'media') {
        setFormData({
          section_key: 'hero',
          title: '',
          description: '',
          order: 0
        });
      } else if (editType === 'text' && currentEdit) {
        setFormData({
          section_key: currentEdit.section_key || '',
          key: currentEdit.key || '',
          content_markdown: currentEdit.content_markdown || '',
          type: currentEdit.type || 'text'
        });
      } else if (editType === 'user' && currentEdit) {
        setFormData({
          email: currentEdit.email || '',
          password: '', // Don't prefill password for security
          roles: currentEdit.roles || ['admin']
        });
      } else if (editType === 'user') {
        setFormData({
          email: '',
          password: '',
          roles: ['admin']
        });
      }
    }
  }, [open, editType, currentEdit]);

  const handleSave = () => {
    console.log('HandleSave called with editType:', editType, 'formData:', formData);
    
    if (editType === 'plan') {
      // Remove empty benefits
      const cleanedBenefits = formData.benefits.filter(benefit => benefit.trim() !== '');
      onSave({ ...formData, benefits: cleanedBenefits });
    } else if (editType === 'media' && selectedFile) {
      onSave({ ...formData, file: selectedFile });
    } else if (editType === 'text') {
      console.log('Saving text with formData:', formData);
      onSave(formData);
    } else if (editType === 'user') {
      onSave(formData);
    }
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const addBenefit = () => {
    setFormData({ ...formData, benefits: [...formData.benefits, ''] });
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: newBenefits });
  };

  const toggleRole = (role) => {
    const roles = formData.roles || [];
    const newRoles = roles.includes(role) 
      ? roles.filter(r => r !== role)
      : [...roles, role];
    setFormData({ ...formData, roles: newRoles });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editType === 'plan' 
              ? (currentEdit ? 'Editar Plan' : 'Crear Nuevo Plan')
              : editType === 'media' 
              ? 'Subir Archivo'
              : editType === 'text'
              ? 'Editar Texto'
              : editType === 'user'
              ? (currentEdit ? 'Editar Usuario' : 'Crear Nuevo Usuario')
              : 'Editar'
            }
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Plan Form */}
          {editType === 'plan' && (
            <>
              <div className="space-y-2">
                <Label>Nombre del Plan</Label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Protección Básica"
                />
              </div>

              <div className="space-y-2">
                <Label>Precio Mensual (Q)</Label>
                <Input
                  type="number"
                  value={formData.price_q_monthly || ''}
                  onChange={(e) => setFormData({ ...formData, price_q_monthly: parseFloat(e.target.value) || 0 })}
                  placeholder="199"
                />
              </div>

              <div className="space-y-2">
                <Label>Beneficios</Label>
                {formData.benefits?.map((benefit, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder={`Beneficio ${index + 1}`}
                    />
                    {formData.benefits.length > 1 && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => removeBenefit(index)}
                        className="text-red-600"
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" onClick={addBenefit} variant="outline">
                  Agregar Beneficio
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_popular"
                  checked={formData.is_popular || false}
                  onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                />
                <Label htmlFor="is_popular">Marcar como plan popular</Label>
              </div>
            </>
          )}

          {/* Media Upload Form */}
          {editType === 'media' && (
            <>
              <div className="space-y-2">
                <Label>Archivo</Label>
                <Input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
                <p className="text-xs text-gray-500">
                  Formatos soportados: JPG, PNG, GIF, WebP para imágenes. MP4, AVI, MOV, WebM para videos.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Sección</Label>
                <select
                  value={formData.section_key || 'hero'}
                  onChange={(e) => setFormData({ ...formData, section_key: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="hero">Hero</option>
                  <option value="como_funciona">¿Cómo Funciona?</option>
                  <option value="testimonios">Testimonios</option>
                  <option value="planes">Planes</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Título (opcional)</Label>
                <Input
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título descriptivo del archivo"
                />
              </div>

              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del archivo"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={formData.order || 0}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </>
          )}

          {/* Text Edit Form */}
          {editType === 'text' && (
            <>
              <div className="space-y-2">
                <Label>Sección: {formData.section_key}</Label>
                <Label>Campo: {formData.key}</Label>
              </div>

              <div className="space-y-2">
                <Label>Contenido</Label>
                <Textarea
                  value={formData.content_markdown || ''}
                  onChange={(e) => setFormData({ ...formData, content_markdown: e.target.value })}
                  placeholder="Contenido del texto..."
                  rows={6}
                />
                <p className="text-xs text-gray-500">
                  Puedes usar Markdown para formatear el texto
                </p>
              </div>
            </>
          )}

          {/* User Form */}
          {editType === 'user' && (
            <>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="usuario@protegeya.gt"
                />
              </div>

              <div className="space-y-2">
                <Label>{currentEdit ? 'Nueva Contraseña (opcional)' : 'Contraseña'}</Label>
                <Input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={currentEdit ? 'Dejar en blanco para no cambiar' : 'Contraseña segura'}
                />
              </div>

              <div className="space-y-2">
                <Label>Roles</Label>
                <div className="space-y-2">
                  {['admin', 'editor', 'viewer'].map(role => (
                    <label key={role} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.roles?.includes(role) || false}
                        onChange={() => toggleRole(role)}
                      />
                      <span className="text-sm capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={
              (editType === 'plan' && !formData.name) || 
              (editType === 'media' && !selectedFile) ||
              (editType === 'text' && !formData.content_markdown?.trim()) ||
              (editType === 'user' && (!formData.email || (!currentEdit && !formData.password)))
            }
            style={{backgroundColor: '#22C55E'}}
          >
            {currentEdit ? 'Actualizar' : 'Crear'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Modal para editar corredor
const EditCorredorModal = ({ corredor, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    nombre_completo: corredor.nombre_completo,
    email: corredor.email,
    whatsapp: corredor.whatsapp,
    departamento: corredor.departamento,
    municipio: corredor.municipio,
    tipo_trabajo: corredor.tipo_trabajo,
    nombre_empresa: corredor.nombre_empresa || '',
    anos_experiencia: corredor.anos_experiencia,
    tipos_seguros: corredor.tipos_seguros,
    plan_seleccionado: corredor.plan_seleccionado,
    acepta_terminos: corredor.acepta_terminos,
    acepta_seguimiento: corredor.acepta_seguimiento
  });
  
  const [departamentos, setDepartamentos] = useState([]);

  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const response = await axios.get(`${API}/departamentos`);
        setDepartamentos(response.data.departamentos);
      } catch (error) {
        console.error('Error loading departamentos:', error);
      }
    };
    fetchDepartamentos();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'tipos_seguros') {
        const updatedTipos = checked
          ? [...formData.tipos_seguros, value]
          : formData.tipos_seguros.filter(tipo => tipo !== value);
        setFormData(prev => ({ ...prev, tipos_seguros: updatedTipos }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validations
    if (formData.tipos_seguros.length === 0) {
      toast.error('Debe seleccionar al menos un tipo de seguro');
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Corredor</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos Personales */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos Personales</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                <Input
                  id="nombre_completo"
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Correo Electrónico *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="whatsapp">Número de WhatsApp *</Label>
                <Input
                  id="whatsapp"
                  name="whatsapp"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="departamento">Departamento *</Label>
                <select
                  id="departamento"
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleInputChange}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="">Seleccione un departamento</option>
                  {departamentos.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="municipio">Municipio o Zona de Atención *</Label>
                <Input
                  id="municipio"
                  name="municipio"
                  value={formData.municipio}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Experiencia */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Experiencia Profesional</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Tipo de Trabajo *</Label>
                <div className="flex gap-4 mt-2">
                  {['Independiente', 'Agencia', 'Aseguradora'].map(tipo => (
                    <label key={tipo} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="tipo_trabajo"
                        value={tipo}
                        checked={formData.tipo_trabajo === tipo}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm">{tipo}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(formData.tipo_trabajo === 'Agencia' || formData.tipo_trabajo === 'Aseguradora') && (
                <div>
                  <Label htmlFor="nombre_empresa">Nombre de {formData.tipo_trabajo}</Label>
                  <Input
                    id="nombre_empresa"
                    name="nombre_empresa"
                    value={formData.nombre_empresa}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="anos_experiencia">Años de Experiencia *</Label>
                <select
                  id="anos_experiencia"
                  name="anos_experiencia"
                  value={formData.anos_experiencia}
                  onChange={handleInputChange}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="">Seleccione experiencia</option>
                  <option value="<1">Menos de 1 año</option>
                  <option value="1-3">1 a 3 años</option>
                  <option value="4-6">4 a 6 años</option>
                  <option value="7+">7 años o más</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-medium">Tipos de Seguros que Ofrece *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {['Auto', 'Vida', 'Médicos', 'Daños', 'Otros'].map(tipo => (
                    <label key={tipo} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="tipos_seguros"
                        value={tipo}
                        checked={formData.tipos_seguros.includes(tipo)}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm">{tipo}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="plan_seleccionado">Plan *</Label>
                <select
                  id="plan_seleccionado"
                  name="plan_seleccionado"
                  value={formData.plan_seleccionado}
                  onChange={handleInputChange}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="Mensual">Mensual</option>
                  <option value="Semestral">Semestral</option>
                  <option value="Anual">Anual</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">
              Guardar Cambios
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Componente para gestionar corredores en el admin
const CorredoresTab = () => {
  const [corredores, setCorredores] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [error, setError] = useState(null);
  const [editingCorrector, setEditingCorrector] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchCorredores();
    fetchStats();
  }, []);

  const fetchCorredores = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Fetching corredores with token:', token ? 'Token exists' : 'No token');
      
      if (!token) {
        console.error('No admin token found');
        toast.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      const response = await axios.get(`${API}/admin/corredores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Corredores loaded:', response.data.length);
      setCorredores(response.data);
    } catch (error) {
      console.error('Error loading corredores:', error);
      if (error.response?.status === 401) {
        setError('Sesión expirada. Por favor, inicie sesión nuevamente.');
        toast.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
        localStorage.removeItem('admin_token');
      } else {
        setError('Error cargando corredores. Intente nuevamente.');
        toast.error('Error cargando corredores');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.error('No admin token found for stats');
        return;
      }
      
      const response = await axios.get(`${API}/admin/corredores/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Stats loaded:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      if (error.response?.status === 401) {
        console.error('Unauthorized access to stats endpoint');
        localStorage.removeItem('admin_token');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-GT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditCorrector = async (corredorData) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.put(
        `${API}/admin/corredores/${editingCorrector.id}`,
        corredorData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update corredor in local state
      setCorredores(prev => prev.map(c => 
        c.id === editingCorrector.id ? response.data : c
      ));
      
      setEditingCorrector(null);
      toast.success('Corredor actualizado exitosamente');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error updating corredor:', error);
      toast.error(error.response?.data?.detail || 'Error actualizando corredor');
    }
  };

  const handleDeleteCorrector = async (corredorId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/corredores/${corredorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove corredor from local state
      setCorredores(prev => prev.filter(c => c.id !== corredorId));
      
      setDeleteConfirm(null);
      toast.success('Corredor eliminado exitosamente');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error deleting corredor:', error);
      toast.error(error.response?.data?.detail || 'Error eliminando corredor');
    }
  };

  const handleStatusChange = async (corredorId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/corredores/${corredorId}/status`,
        { estado_pago: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update corredor status in local state
      setCorredores(prev => prev.map(c => 
        c.id === corredorId ? { ...c, estado_pago: newStatus } : c
      ));
      
      toast.success('Estado actualizado exitosamente');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Error actualizando estado');
    }
  };

  const filteredCorredores = selectedPlan === 'all' 
    ? corredores 
    : corredores.filter(c => c.plan_seleccionado === selectedPlan);

  if (loading) {
    return <div className="flex items-center justify-center py-8">Cargando corredores...</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <div className="text-red-600 text-center">{error}</div>
        <Button onClick={() => {
          setError(null);
          setLoading(true);
          fetchCorredores();
          fetchStats();
        }}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Corredores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_corredores}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Pagos Pendientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.por_estado.pendiente}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Pagos Completados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.por_estado.pagado}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Últimos 7 días</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.registros_recientes}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros y Lista */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Corredores Registrados</CardTitle>
              <CardDescription>
                Gestiona todos los corredores de seguros registrados en la plataforma
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="plan-filter">Filtrar por plan:</Label>
              <select
                id="plan-filter"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="border rounded px-2 py-1"
              >
                <option value="all">Todos los planes</option>
                <option value="Mensual">Mensual</option>
                <option value="Semestral">Semestral</option>
                <option value="Anual">Anual</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCorredores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay corredores registrados
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCorredores.map((corredor) => (
                <div key={corredor.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{corredor.nombre_completo}</h3>
                      <p className="text-gray-600">{corredor.email}</p>
                      <p className="text-gray-600">{corredor.whatsapp}</p>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        corredor.estado_pago === 'pagado' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {corredor.estado_pago === 'pagado' ? 'Pagado' : 'Pendiente'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Plan:</span> {corredor.plan_seleccionado}
                    </div>
                    <div>
                      <span className="font-medium">Ubicación:</span> {corredor.municipio}, {corredor.departamento}
                    </div>
                    <div>
                      <span className="font-medium">Experiencia:</span> {corredor.anos_experiencia} años
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Tipo:</span> {corredor.tipo_trabajo}
                      {corredor.nombre_empresa && (
                        <span className="ml-1">({corredor.nombre_empresa})</span>
                      )}
                    </div>
                    <div>
                      <span className="font-medium">Seguros:</span> {corredor.tipos_seguros.join(', ')}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xs text-gray-500">
                      Registrado: {formatDate(corredor.fecha_registro)}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Status Change Dropdown */}
                      <select
                        value={corredor.estado_pago}
                        onChange={(e) => handleStatusChange(corredor.id, e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="pagado">Pagado</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                      
                      {/* Action Buttons */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setEditingCorrector(corredor)}
                      >
                        Editar
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteConfirm(corredor)}
                      >
                        Eliminar
                      </Button>
                      
                      {corredor.recurrente_checkout_url && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(corredor.recurrente_checkout_url, '_blank')}
                        >
                          Ver Checkout
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Corredor Modal */}
      {editingCorrector && (
        <EditCorredorModal 
          corredor={editingCorrector}
          onSave={handleEditCorrector}
          onClose={() => setEditingCorrector(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                ¿Está seguro que desea eliminar al corredor <strong>{deleteConfirm.nombre_completo}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleDeleteCorrector(deleteConfirm.id)}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Componente para el registro de corredores
const CorredorRegistroPage = () => {
  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    whatsapp: '',
    departamento: '',
    municipio: '',
    tipo_trabajo: '',
    nombre_empresa: '',
    anos_experiencia: '',
    tipos_seguros: [],
    metodo_pago_preferido: 'tarjeta',
    acepta_terminos: false,
    acepta_seguimiento: false
  });
  
  const [departamentos, setDepartamentos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [registroExitoso, setRegistroExitoso] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState('');

  // Obtener el plan de los parámetros de la URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const plan = urlParams.get('plan');
    if (plan) {
      setPlanSeleccionado(plan.charAt(0).toUpperCase() + plan.slice(1));
    }
  }, []);

  // Cargar departamentos
  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const response = await axios.get(`${API}/departamentos`);
        setDepartamentos(response.data.departamentos);
      } catch (error) {
        console.error('Error loading departamentos:', error);
      }
    };
    fetchDepartamentos();
  }, []);

  // Información de los planes
  const planInfo = {
    'Mensual': { precio: 'Q499', leads: '20' },
    'Semestral': { precio: 'Q2,399', leads: '30' },
    'Anual': { precio: 'Q4,788', leads: '40' }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'tipos_seguros') {
        const updatedTipos = checked
          ? [...formData.tipos_seguros, value]
          : formData.tipos_seguros.filter(tipo => tipo !== value);
        setFormData(prev => ({ ...prev, tipos_seguros: updatedTipos }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!planSeleccionado) {
      toast.error('No se ha seleccionado un plan válido');
      return;
    }

    if (!formData.acepta_terminos || !formData.acepta_seguimiento) {
      toast.error('Debe aceptar los términos y condiciones y el compromiso de seguimiento');
      return;
    }

    if (formData.tipos_seguros.length === 0) {
      toast.error('Debe seleccionar al menos un tipo de seguro');
      return;
    }

    setIsLoading(true);

    try {
      // Crear el corredor
      const corredorData = {
        ...formData,
        plan_seleccionado: planSeleccionado
      };

      const response = await axios.post(`${API}/corredores`, corredorData);
      
      // Mostrar página de éxito
      setRegistroExitoso(true);
      toast.success('¡Registro exitoso! Pronto nos pondremos en contacto contigo.');

    } catch (error) {
      console.error('Error al registrar corredor:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el registro');
    } finally {
      setIsLoading(false);
    }
  };

  // Página de éxito
  if (registroExitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-3xl text-green-600 mb-2">
                  ¡Registro Exitoso!
                </CardTitle>
                <CardDescription className="text-lg">
                  Tu solicitud ha sido recibida correctamente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-6 bg-blue-50 rounded-lg text-left">
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">¿Qué sigue ahora?</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">1.</span>
                      <span>Nuestro equipo revisará tu solicitud en las próximas 24 horas.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">2.</span>
                      <span>Te contactaremos por WhatsApp para activar tu suscripción y coordinar el pago.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">3.</span>
                      <span>Una vez confirmado el pago, tendrás acceso inmediato a tu panel de corredor.</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-green-700">📱 Importante:</span> Mantén tu WhatsApp activo para que podamos contactarte rápidamente.
                  </p>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={() => window.location.href = '/'}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                  >
                    Volver a la Página Principal
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/'}
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Volver a la Landing
              </Button>
              <div className="flex-1"></div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Registro de Corredor de Seguros
            </h1>
            <p className="text-xl text-gray-600">
              Complete sus datos para unirse a nuestra red de corredores
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Formulario */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Información del Corredor</CardTitle>
                  <CardDescription>
                    Complete todos los campos obligatorios para continuar con su registro
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Datos Personales */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos Personales</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                          <Input
                            id="nombre_completo"
                            name="nombre_completo"
                            value={formData.nombre_completo}
                            onChange={handleInputChange}
                            required
                            placeholder="Ingrese su nombre completo"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Correo Electrónico *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="correo@ejemplo.com"
                          />
                        </div>
                        <div>
                          <Label htmlFor="whatsapp">Número de WhatsApp *</Label>
                          <Input
                            id="whatsapp"
                            name="whatsapp"
                            type="tel"
                            value={formData.whatsapp}
                            onChange={handleInputChange}
                            required
                            placeholder="+502 0000-0000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="departamento">Departamento *</Label>
                          <select
                            id="departamento"
                            name="departamento"
                            value={formData.departamento}
                            onChange={handleInputChange}
                            required
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                          >
                            <option value="">Seleccione un departamento</option>
                            {departamentos.map(dept => (
                              <option key={dept} value={dept}>{dept}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="municipio">Municipio o Zona de Atención *</Label>
                          <Input
                            id="municipio"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleInputChange}
                            required
                            placeholder="Ingrese municipio o zona donde atiende"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Experiencia */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Experiencia Profesional</h3>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">¿Trabajás de forma independiente o en agencia? *</Label>
                          <div className="flex gap-4 mt-2">
                            {['Independiente', 'Agencia', 'Aseguradora'].map(tipo => (
                              <label key={tipo} className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  name="tipo_trabajo"
                                  value={tipo}
                                  checked={formData.tipo_trabajo === tipo}
                                  onChange={handleInputChange}
                                  required
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm">{tipo}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {(formData.tipo_trabajo === 'Agencia' || formData.tipo_trabajo === 'Aseguradora') && (
                          <div>
                            <Label htmlFor="nombre_empresa">Nombre de {formData.tipo_trabajo}</Label>
                            <Input
                              id="nombre_empresa"
                              name="nombre_empresa"
                              value={formData.nombre_empresa}
                              onChange={handleInputChange}
                              placeholder={`Nombre de la ${formData.tipo_trabajo.toLowerCase()}`}
                            />
                          </div>
                        )}

                        <div>
                          <Label htmlFor="anos_experiencia">Años de Experiencia *</Label>
                          <select
                            id="anos_experiencia"
                            name="anos_experiencia"
                            value={formData.anos_experiencia}
                            onChange={handleInputChange}
                            required
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                          >
                            <option value="">Seleccione experiencia</option>
                            <option value="<1">Menos de 1 año</option>
                            <option value="1-3">1 a 3 años</option>
                            <option value="4-6">4 a 6 años</option>
                            <option value="7+">7 años o más</option>
                          </select>
                        </div>

                        <div>
                          <Label className="text-sm font-medium">Tipos de Seguros que Ofrece *</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {['Auto', 'Vida', 'Médicos', 'Daños', 'Otros'].map(tipo => (
                              <label key={tipo} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  name="tipos_seguros"
                                  value={tipo}
                                  checked={formData.tipos_seguros.includes(tipo)}
                                  onChange={handleInputChange}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm">{tipo}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Método de Pago Preferido */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Método de Pago Preferido</h3>
                      <div className="space-y-3">
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                          <input
                            type="radio"
                            name="metodo_pago_preferido"
                            value="tarjeta"
                            checked={formData.metodo_pago_preferido === 'tarjeta'}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div className="ml-3">
                            <span className="font-semibold text-gray-900">Tarjeta de Débito/Crédito</span>
                            <p className="text-sm text-gray-600">Pago recurrente automático</p>
                          </div>
                        </label>
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                          <input
                            type="radio"
                            name="metodo_pago_preferido"
                            value="transferencia"
                            checked={formData.metodo_pago_preferido === 'transferencia'}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div className="ml-3">
                            <span className="font-semibold text-gray-900">Transferencia Bancaria</span>
                            <p className="text-sm text-gray-600">Pago manual por transferencia</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Términos y Condiciones */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="acepta_terminos"
                          name="acepta_terminos"
                          checked={formData.acepta_terminos}
                          onChange={handleInputChange}
                          required
                          className="w-4 h-4 text-blue-600 mt-1"
                        />
                        <label htmlFor="acepta_terminos" className="text-sm text-gray-700">
                          Acepto los términos y condiciones de uso de ProtegeYA como corredor. *
                        </label>
                      </div>
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="acepta_seguimiento"
                          name="acepta_seguimiento"
                          checked={formData.acepta_seguimiento}
                          onChange={handleInputChange}
                          required
                          className="w-4 h-4 text-blue-600 mt-1"
                        />
                        <label htmlFor="acepta_seguimiento" className="text-sm text-gray-700">
                          Me comprometo a dar seguimiento profesional a los leads asignados. *
                        </label>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg"
                    >
                      {isLoading ? 'Procesando...' : 'Completar Registro'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Resumen del Plan */}
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <CardTitle>Plan Seleccionado</CardTitle>
                </CardHeader>
                <CardContent>
                  {planSeleccionado && planInfo[planSeleccionado] ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-blue-600">
                          Plan {planSeleccionado}
                        </h3>
                        <div className="text-3xl font-bold text-gray-900 mt-2">
                          {planInfo[planSeleccionado].precio}
                        </div>
                        <p className="text-gray-600">
                          {planSeleccionado === 'Mensual' ? 'por mes' : 
                           planSeleccionado === 'Semestral' ? 'cada 6 meses' : 'por año'}
                        </p>
                      </div>
                      
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-2">Incluye:</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            {planInfo[planSeleccionado].leads} leads por mes
                          </li>
                          <li className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            Soporte técnico incluido
                          </li>
                          <li className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            Panel de gestión web
                          </li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500">
                      <p>No se ha seleccionado un plan válido</p>
                      <Button 
                        variant="outline" 
                        onClick={() => window.location.href = '/#planes'}
                        className="mt-2"
                      >
                        Seleccionar Plan
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  useEffect(() => {
    // Check if admin is logged in and validate token
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Validate token by making a test request
      axios.get(`${API}/admin/texts`)
        .then(() => {
          console.log('Token is valid, user logged in');
          setIsAdminLoggedIn(true);
        })
        .catch(() => {
          console.log('Token validation failed in useEffect, but proceeding anyway (temporary fix)');
          // Temporary: proceed with login even if validation fails
          setIsAdminLoggedIn(true);
        });
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAdminLoggedIn(false);
    window.location.href = '/admin';
  };

  // Admin route component
  const AdminRoute = () => {
    return (
      <div className="min-h-screen bg-gray-50">
        {!isAdminLoggedIn ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-lg">
              <h1 className="text-2xl font-bold mb-4 text-center">ProtegeYA Admin</h1>
              <p className="text-gray-600 mb-4 text-center">Página de login funcionando</p>
              <AdminLogin onLogin={setIsAdminLoggedIn} />
            </div>
          </div>
        ) : (
          <AdminPanel onLogout={handleLogout} />
        )}
      </div>
    );
  };

  // All routes (public and admin)
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          <Route path="/planes" element={<PlanesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/registro-corredor" element={<CorredorRegistroPage />} />
          <Route path="/admin" element={<AdminRoute />} />
        </Routes>
        <WhatsAppWidget />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
